import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, DateTime, Icon, InlineTooltip, Page, Table, Tooltip } from '~/components';
import { TableBoxRowActions } from '~/components/table';
import { useApi, useSession, useToast, useWorkspace } from '~/contexts';
import { useFeatures, useIsMounted } from '~/hooks';
import { colors } from '~/styles';
import TeamsMembersDrawer from './TeamsMembersDrawer';
import TeamsNotificationsDrawer from './TeamsNotificationsDrawer';
import TeamsProjectsDrawer from './TeamsProjectsDrawer';

const MARKETPLACE_URL = 'https://marketplace.microsoft.com/en-us/product/WA200012456';
const OUTLOOK_CALENDAR_HELP_URL = 'https://help.ruddr.io/integrations/microsoft-outlook-calendar-integration';
const COPILOT_HELP_URL = 'https://help.ruddr.io/integrations/ai-connectors-microsoft-copilot-setup';

const Loading = () => <Icon icon="spinner" spin />;

const OK = () => <Icon icon="check" color={colors.success} />;

const Error = ({ message }) => (
  <Tooltip message={message}>
    <Icon icon="exclamation-circle" color={colors.danger} />
  </Tooltip>
);

const Status = ({ status, message }) => {
  switch (status) {
    case 'ready':
      return <OK />;

    case 'error':
      return <Error message={message} />;

    default:
      return <Loading />;
  }
};

export default function MicrosoftSettingsPage() {
  const { workspace } = useWorkspace();
  const api = useApi();
  const isMounted = useIsMounted();
  const toast = useToast();
  const features = useFeatures();
  const { isImpersonation } = useSession();

  const [dialog, setDialog] = useState(null);
  const closeDialog = () => {
    setDialog(null);
  };

  const [integration, setIntegration] = useState({ data: null, status: 'loading', message: null });

  const loadIntegration = useCallback(async () => {
    setIntegration((state) => ({ ...state, status: 'loading' }));

    try {
      const { data } = await api.www.workspaces(workspace.id).integrations().microsoft.teams.get();

      if (!isMounted.current) return;
      setIntegration({ data, status: 'ready', message: null });
    } catch (error) {
      setIntegration({ data: null, status: 'error', message: error.message });
    }
  }, [api, workspace.id, isMounted]);

  useEffect(() => {
    loadIntegration();
  }, [loadIntegration]);

  const handleGrantConsent = async () => {
    try {
      const { data } = await api.www.workspaces(workspace.id).integrations().microsoft.connection.getAdminConsentUri();
      window.location.href = data.adminConsentUri;
    } catch (error) {
      toast.error(error.message || 'Consent could not be started. Please try again.');
    }
  };

  const [members, setMembers] = useState({ data: null, status: 'loading', message: null });

  const loadMembers = useCallback(async () => {
    setMembers((state) => ({ ...state, status: 'loading' }));

    try {
      const { data } = await api.www.workspaces(workspace.id).integrations().microsoft.teams.getMembers();

      if (!isMounted.current) return;
      setMembers({ data: data.members.filter((member) => member.link), status: 'ready', message: null });
    } catch (error) {
      setMembers({ data: null, status: 'error', message: error.message });
    }
  }, [api, workspace.id, isMounted]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const handleCloseMembers = async () => {
    await loadMembers();
    closeDialog();
  };

  const handleCloseNotifications = async () => {
    await loadIntegration();
    closeDialog();
  };

  const connection = integration.data?.connection;
  const { projects, notifications } = integration.data?.teams ?? {};
  const shownNotifications =
    notifications && (features.timesheets ? notifications : _.omit(notifications, 'unsubmittedTimesheetReminders'));

  const disabled = integration.status !== 'ready';

  return (
    <>
      <Page.Section>
        <Table>
          <Table.Header>
            <Table.Column width="14rem">Category</Table.Column>
            <Table.Column width="8rem" align="center">
              Status
            </Table.Column>
            <Table.Column>Details</Table.Column>
            <Table.BoxActionsColumn />
          </Table.Header>

          <Table.Body>
            <Table.BoxRow data-testid="ms-organization">
              <Table.Cell>
                <strong>Organization</strong>
              </Table.Cell>
              <Table.Cell>
                <Status status={integration.status} message={integration.message} />
              </Table.Cell>
              <Table.Cell>
                {connection && (
                  <span>
                    {connection.tenantDomain}.{' '}
                    {connection.connectedByMemberName
                      ? `Connected by ${connection.connectedByMemberName} on `
                      : 'Connected on '}
                    <DateTime value={connection.createdAt} />.
                  </span>
                )}
              </Table.Cell>

              <TableBoxRowActions />
            </Table.BoxRow>

            <Table.BoxRow data-testid="ms-deployment">
              <Table.Cell>
                <strong>Deployment</strong>
              </Table.Cell>
              <Table.Cell />
              <Table.Cell>
                Optional. A Microsoft 365 admin can pre-install Ruddr in Teams and Copilot for everyone. Otherwise
                members add Ruddr from the Teams store themselves.
              </Table.Cell>

              <TableBoxRowActions>
                <Button as="a" className="button" href={MARKETPLACE_URL} target="_blank" rel="noopener noreferrer">
                  <Icon icon="external-link-alt" />
                  <InlineTooltip message="Open Ruddr in Microsoft Marketplace" placement="left" />
                </Button>
              </TableBoxRowActions>
            </Table.BoxRow>

            <Table.BoxRow data-testid="ms-admin-consent">
              <Table.Cell>
                <strong>Consent</strong>
              </Table.Cell>
              <Table.Cell>
                {connection ? (
                  connection.adminConsentedAt && <OK />
                ) : (
                  <Status status={integration.status} message={integration.message} />
                )}
              </Table.Cell>
              <Table.Cell>
                {connection &&
                  (connection.adminConsentedAt ? (
                    <span>
                      Granted on <DateTime value={connection.adminConsentedAt} />.
                    </span>
                  ) : (
                    <>
                      Optional. A Microsoft 365 admin can approve Ruddr&apos;s calendar permissions once for everyone,
                      so members don&apos;t see a consent prompt when they connect their calendar.
                    </>
                  ))}
              </Table.Cell>

              <TableBoxRowActions>
                {!isImpersonation && !connection?.adminConsentedAt && (
                  <Button disabled={disabled} onClick={handleGrantConsent}>
                    <Icon icon="shield-check" />
                    <InlineTooltip message="Grant consent for your organization" placement="left" />
                  </Button>
                )}
              </TableBoxRowActions>
            </Table.BoxRow>

            <Table.BoxRow data-testid="ms-teams-members">
              <Table.Cell>
                <strong>Teams Members</strong>
              </Table.Cell>
              <Table.Cell>
                <Status status={members.status} message={members.message} />
              </Table.Cell>
              <Table.Cell>
                {members.data && `${members.data.length} ${members.data.length === 1 ? 'member' : 'members'} linked`}
              </Table.Cell>

              <TableBoxRowActions>
                <TableBoxRowActions.Edit disabled={disabled} onClick={() => setDialog('members')} />
              </TableBoxRowActions>
            </Table.BoxRow>

            <Table.BoxRow data-testid="ms-teams-projects">
              <Table.Cell>
                <strong>Teams Projects</strong>
              </Table.Cell>
              <Table.Cell>
                <Status status={integration.status} message={integration.message} />
              </Table.Cell>
              <Table.Cell>{projects && `${projects.connected} of ${projects.total} projects connected`}</Table.Cell>

              <TableBoxRowActions>
                <TableBoxRowActions.View disabled={disabled} onClick={() => setDialog('projects')} />
              </TableBoxRowActions>
            </Table.BoxRow>

            <Table.BoxRow data-testid="ms-teams-notifications">
              <Table.Cell>
                <strong>Teams Notifications</strong>
              </Table.Cell>
              <Table.Cell>
                <Status status={integration.status} message={integration.message} />
              </Table.Cell>
              <Table.Cell>
                {shownNotifications &&
                  `${Object.values(shownNotifications).filter(Boolean).length} of ${
                    Object.keys(shownNotifications).length
                  } notifications on`}
              </Table.Cell>

              <TableBoxRowActions>
                <TableBoxRowActions.Edit disabled={disabled} onClick={() => setDialog('notifications')} />
              </TableBoxRowActions>
            </Table.BoxRow>

            <Table.BoxRow>
              <Table.Cell>
                <strong>Outlook Calendar</strong>
              </Table.Cell>
              <Table.Cell />
              <Table.Cell>
                <span>
                  Each member connects their own calendar from{' '}
                  <Link to={`/app/${workspace.key}/my-profile/calendar`}>My Profile</Link>.{' '}
                  <a href={OUTLOOK_CALENDAR_HELP_URL} target="_blank" rel="noopener noreferrer">
                    Learn more
                  </a>
                </span>
              </Table.Cell>

              <TableBoxRowActions />
            </Table.BoxRow>

            <Table.BoxRow>
              <Table.Cell>
                <strong>Copilot</strong>
              </Table.Cell>
              <Table.Cell />
              <Table.Cell>
                <span>
                  Members add Ruddr in Microsoft 365 Copilot to ask about their Ruddr work. Requires the Ruddr Assistant
                  to be enabled for this workspace.{' '}
                  <a href={COPILOT_HELP_URL} target="_blank" rel="noopener noreferrer">
                    Learn more
                  </a>
                </span>
              </Table.Cell>

              <TableBoxRowActions />
            </Table.BoxRow>
          </Table.Body>
        </Table>
      </Page.Section>

      {dialog &&
        {
          members: () => <TeamsMembersDrawer onClose={handleCloseMembers} />,
          projects: () => <TeamsProjectsDrawer onClose={closeDialog} />,
          notifications: () => (
            <TeamsNotificationsDrawer notifications={notifications} onClose={handleCloseNotifications} />
          ),
        }[dialog]()}
    </>
  );
}
